/**
 * Contracts for a VM owned by the current T3 environment.
 *
 * The worker stays on the host loopback interface. Remote clients only see
 * these authenticated T3 RPCs, so LAN, Tailscale, and Fabrica Connect all use the
 * same transport.
 */
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import {
  IsoDateTime,
  PortSchema,
  PositiveInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const VmProfileId = TrimmedNonEmptyString.pipe(Schema.brand("VmProfileId"));
export type VmProfileId = typeof VmProfileId.Type;

export const VmBuildId = TrimmedNonEmptyString.pipe(Schema.brand("VmBuildId"));
export type VmBuildId = typeof VmBuildId.Type;

export const VmGitObjectId = TrimmedNonEmptyString.check(
  Schema.isPattern(/^(?:[0-9a-fA-F]{40}|[0-9a-fA-F]{64})$/),
).pipe(Schema.brand("VmGitObjectId"));
export type VmGitObjectId = typeof VmGitObjectId.Type;

const VmRecipeCommand = Schema.String.check(Schema.isMaxLength(32_768));

export const ProjectVmResources = Schema.Struct({
  vcpu: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 16 })),
  memoryMiB: Schema.Int.check(Schema.isBetween({ minimum: 1024, maximum: 65_536 })),
  diskGiB: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 8, maximum: 2_048 }))),
});
export type ProjectVmResources = typeof ProjectVmResources.Type;

export const ProjectVmPort = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
  guestPort: PortSchema,
  protocol: Schema.Literals(["http", "tcp"]),
  visibility: Schema.Literals(["private", "project"]),
});
export type ProjectVmPort = typeof ProjectVmPort.Type;

export const ProjectVmVerification = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  command: TrimmedNonEmptyString.check(Schema.isMaxLength(32_768)),
  timeoutSeconds: Schema.optional(PositiveInt.check(Schema.isLessThanOrEqualTo(3_600))),
});
export type ProjectVmVerification = typeof ProjectVmVerification.Type;

const VM_MANAGED_SECRET_NAMES: ReadonlySet<string> = new Set([
  "_",
  "CODEX_HOME",
  "DISPLAY",
  "HOME",
  "HOSTNAME",
  "LOGNAME",
  "OLDPWD",
  "PATH",
  "PWD",
  "SHELL",
  "SHLVL",
  "TERM",
  "TMPDIR",
  "USER",
]);

export function isVmManagedSecretName(name: string): boolean {
  return VM_MANAGED_SECRET_NAMES.has(name) || name.startsWith("T3_") || name.startsWith("T3CODE_");
}

export const ProjectVmSecretName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(256),
  Schema.isPattern(/^[A-Za-z_][A-Za-z0-9_]*$/),
  Schema.makeFilter((name) =>
    isVmManagedSecretName(name) ? "This environment variable is managed by the VM." : undefined,
  ),
);
export type ProjectVmSecretName = typeof ProjectVmSecretName.Type;

export const ProjectVmSecretValue = Schema.Struct({
  name: ProjectVmSecretName,
  value: Schema.String.check(
    Schema.isMaxLength(65_536),
    Schema.makeFilter((value) =>
      value.includes("\u0000") ? "Secret values cannot contain NUL bytes." : undefined,
    ),
  ),
});
export type ProjectVmSecretValue = typeof ProjectVmSecretValue.Type;

export const ProjectVmSecretSummary = Schema.Struct({
  name: ProjectVmSecretName,
  configured: Schema.Boolean,
});
export type ProjectVmSecretSummary = typeof ProjectVmSecretSummary.Type;

/**
 * A project VM recipe contains references to secret names, never their values.
 * Build and task-time secret storage remains outside the event stream.
 */
export const ProjectVmProfileConfig = Schema.Struct({
  install: VmRecipeCommand,
  updateContent: VmRecipeCommand,
  start: VmRecipeCommand,
  resources: ProjectVmResources,
  ports: Schema.Array(ProjectVmPort),
  verification: Schema.Array(ProjectVmVerification),
  secretNames: Schema.Array(ProjectVmSecretName).pipe(
    Schema.withDecodingDefaultKey(Effect.succeed([])),
  ),
});
export type ProjectVmProfileConfig = typeof ProjectVmProfileConfig.Type;

export const VmBuildState = Schema.Literals([
  "queued",
  "preparing",
  "installing",
  "verifying",
  "snapshotting",
  "restore-testing",
  "ready",
  "failed",
  "superseded",
  "deleting",
]);
export type VmBuildState = typeof VmBuildState.Type;

export const VmBuild = Schema.Struct({
  id: VmBuildId,
  profileId: VmProfileId,
  profileVersion: PositiveInt,
  state: VmBuildState,
  sourceSandboxId: TrimmedNonEmptyString,
  image: Schema.NullOr(TrimmedNonEmptyString),
  resultImageDigest: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  error: Schema.NullOr(Schema.String),
});
export type VmBuild = typeof VmBuild.Type;

export const ProjectVmProfile = Schema.Struct({
  id: VmProfileId,
  projectId: ProjectId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  version: PositiveInt,
  config: ProjectVmProfileConfig,
  activeBuildId: Schema.NullOr(VmBuildId),
  builds: Schema.Array(VmBuild),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ProjectVmProfile = typeof ProjectVmProfile.Type;

export const ProjectVmState = Schema.Struct({
  projectId: ProjectId,
  profile: Schema.NullOr(ProjectVmProfile),
});
export type ProjectVmState = typeof ProjectVmState.Type;

export const ProjectVmStateInput = Schema.Struct({
  projectId: ProjectId,
});
export type ProjectVmStateInput = typeof ProjectVmStateInput.Type;

export const ProjectVmProfileConfigureInput = Schema.Struct({
  projectId: ProjectId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  config: ProjectVmProfileConfig,
});
export type ProjectVmProfileConfigureInput = typeof ProjectVmProfileConfigureInput.Type;

/**
 * Structured handoff written by the setup agent in its isolated worktree.
 *
 * The file contains secret names only. Values remain in the host secret store
 * and never enter the agent-authored proposal or orchestration history.
 */
export const PROJECT_VM_PROFILE_PROPOSAL_PATH = ".t3/vm-profile-proposal.json";
const ProjectVmProposalRequirement = TrimmedNonEmptyString.check(Schema.isMaxLength(1_024));
const ProjectVmProposalCheck = TrimmedNonEmptyString.check(Schema.isMaxLength(32_768));
const ProjectVmProposalRequirements = Schema.Struct({
  basePackages: Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(256))).check(
    Schema.isMaxLength(256),
  ),
  projectChanges: Schema.Array(ProjectVmProposalRequirement).check(Schema.isMaxLength(64)),
});
const ProjectVmProfileProposalCommon = {
  schemaVersion: Schema.Literal(1),
  requirements: ProjectVmProposalRequirements,
  checksRun: Schema.Array(ProjectVmProposalCheck).check(Schema.isMaxLength(128)),
  assumptions: Schema.Array(ProjectVmProposalRequirement).check(Schema.isMaxLength(32)),
};

export const ProjectVmProfileProposal = Schema.Union([
  Schema.Struct({
    ...ProjectVmProfileProposalCommon,
    status: Schema.Literal("ready"),
    name: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
    config: ProjectVmProfileConfig,
  }),
  Schema.Struct({
    ...ProjectVmProfileProposalCommon,
    status: Schema.Literal("blocked"),
    reason: TrimmedNonEmptyString.check(Schema.isMaxLength(4_096)),
    name: Schema.optional(Schema.Never),
    config: Schema.optional(Schema.Never),
  }),
]);
export type ProjectVmProfileProposal = typeof ProjectVmProfileProposal.Type;

export const ProjectVmProfileProposalGetInput = Schema.Struct({
  projectId: ProjectId,
  threadId: ThreadId,
});
export type ProjectVmProfileProposalGetInput = typeof ProjectVmProfileProposalGetInput.Type;

export const ProjectVmProfileProposalResult = Schema.Struct({
  projectId: ProjectId,
  threadId: ThreadId,
  proposal: Schema.NullOr(ProjectVmProfileProposal),
  workspaceChanges: Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(16_384))).check(
    Schema.isMaxLength(10_000),
  ),
});
export type ProjectVmProfileProposalResult = typeof ProjectVmProfileProposalResult.Type;

export const ProjectVmSecretsListInput = ProjectVmStateInput;
export type ProjectVmSecretsListInput = typeof ProjectVmSecretsListInput.Type;

export const ProjectVmSecretsPutInput = Schema.Struct({
  projectId: ProjectId,
  secrets: Schema.Array(ProjectVmSecretValue).check(Schema.isMaxLength(256)),
});
export type ProjectVmSecretsPutInput = typeof ProjectVmSecretsPutInput.Type;

export const ProjectVmSecretDeleteInput = Schema.Struct({
  projectId: ProjectId,
  name: ProjectVmSecretName,
});
export type ProjectVmSecretDeleteInput = typeof ProjectVmSecretDeleteInput.Type;

export const ProjectVmSecretsResult = Schema.Struct({
  projectId: ProjectId,
  secrets: Schema.Array(ProjectVmSecretSummary),
});
export type ProjectVmSecretsResult = typeof ProjectVmSecretsResult.Type;

export const VmBuildListInput = ProjectVmStateInput;
export type VmBuildListInput = typeof VmBuildListInput.Type;

export const VmBuildListResult = Schema.Struct({
  projectId: ProjectId,
  activeBuildId: Schema.NullOr(VmBuildId),
  builds: Schema.Array(VmBuild),
});
export type VmBuildListResult = typeof VmBuildListResult.Type;

export const VmBuildSnapshotInput = Schema.Struct({
  projectId: ProjectId,
  threadId: ThreadId,
});
export type VmBuildSnapshotInput = typeof VmBuildSnapshotInput.Type;

export const VmBuildActionInput = Schema.Struct({
  projectId: ProjectId,
  buildId: VmBuildId,
});
export type VmBuildActionInput = typeof VmBuildActionInput.Type;

export const VmExecutionMode = Schema.Literals(["host", "vm"]);
export type VmExecutionMode = typeof VmExecutionMode.Type;

export const VmDriver = Schema.Literals(["apple-container"]);
export type VmDriver = typeof VmDriver.Type;

export const VmState = Schema.Literals([
  "unavailable",
  "missing",
  "creating",
  "starting",
  "running",
  "stopped",
  "error",
]);
export type VmState = typeof VmState.Type;

export const VmStatusInput = Schema.Struct({
  threadId: ThreadId,
});
export type VmStatusInput = typeof VmStatusInput.Type;

export const VmEnsureInput = Schema.Struct({
  threadId: ThreadId,
  projectCwd: TrimmedNonEmptyString,
  buildId: Schema.optional(VmBuildId),
  targetRevision: Schema.optional(VmGitObjectId),
  sourceBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  startUrl: Schema.optional(TrimmedNonEmptyString),
  vcpu: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 16 }))),
  memoryMiB: Schema.optional(
    Schema.Int.check(Schema.isBetween({ minimum: 1024, maximum: 65_536 })),
  ),
});
export type VmEnsureInput = typeof VmEnsureInput.Type;

export const VmStatus = Schema.Struct({
  threadId: ThreadId,
  sandboxId: TrimmedNonEmptyString,
  driver: VmDriver,
  state: VmState,
  available: Schema.Boolean,
  workspacePath: Schema.NullOr(Schema.String),
  desktopRunning: Schema.Boolean,
  width: Schema.Int.check(Schema.isGreaterThan(0)),
  height: Schema.Int.check(Schema.isGreaterThan(0)),
  error: Schema.NullOr(Schema.String),
});
export type VmStatus = typeof VmStatus.Type;

export const VmFrame = Schema.Struct({
  mimeType: Schema.Literals(["image/png", "image/jpeg"]),
  dataBase64: Schema.String.check(Schema.isNonEmpty()),
  width: Schema.Int.check(Schema.isGreaterThan(0)),
  height: Schema.Int.check(Schema.isGreaterThan(0)),
  capturedAt: IsoDateTime,
});
export type VmFrame = typeof VmFrame.Type;

/**
 * The VM desktop stream stays on the selected environment's existing origin.
 * The ticket is sent as a websocket subprotocol instead of a query parameter,
 * which keeps the bearer credential out of request URLs and access logs.
 */
export const VM_DESKTOP_STREAM_PATH = "/api/vm/desktop" as const;
export const VM_DESKTOP_STREAM_PROTOCOL = "t3-vm-desktop" as const;

export const VmDesktopOpenInput = VmStatusInput;
export type VmDesktopOpenInput = typeof VmDesktopOpenInput.Type;

export const VmDesktopOpenResult = Schema.Struct({
  path: TrimmedNonEmptyString.check(Schema.isMaxLength(2_048)),
  ticket: TrimmedNonEmptyString.check(Schema.isMaxLength(512)),
  password: Schema.String.check(Schema.isNonEmpty()).check(Schema.isMaxLength(256)),
  expiresAt: IsoDateTime,
});
export type VmDesktopOpenResult = typeof VmDesktopOpenResult.Type;

export const VmDesktopStreamUnavailableReason = Schema.Literals([
  "session_invalid",
  "vm_not_running",
  "desktop_not_running",
  "desktop_transport_unavailable",
]);
export type VmDesktopStreamUnavailableReason = typeof VmDesktopStreamUnavailableReason.Type;

export class VmDesktopStreamUnavailableError extends Schema.TaggedErrorClass<VmDesktopStreamUnavailableError>()(
  "VmDesktopStreamUnavailableError",
  {
    reason: VmDesktopStreamUnavailableReason,
    message: Schema.String,
  },
) {}

export class VmDesktopStreamLimitError extends Schema.TaggedErrorClass<VmDesktopStreamLimitError>()(
  "VmDesktopStreamLimitError",
  {
    scope: Schema.Literals(["connection", "thread", "environment"]),
    limit: PositiveInt,
    message: Schema.String,
  },
) {}

export const VmDesktopOpenError = Schema.Union([
  VmDesktopStreamUnavailableError,
  VmDesktopStreamLimitError,
]);
export type VmDesktopOpenError = typeof VmDesktopOpenError.Type;

export const VmControlMode = Schema.Literals(["agent", "human"]);
export type VmControlMode = typeof VmControlMode.Type;

export const VmControlStatus = Schema.Struct({
  mode: VmControlMode,
  heldByCurrentConnection: Schema.Boolean,
  holderLabel: Schema.NullOr(Schema.String),
  expiresAt: Schema.NullOr(IsoDateTime),
  agentContinuesRunning: Schema.Literal(true),
});
export type VmControlStatus = typeof VmControlStatus.Type;

export const VmDesktopInputAction = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("pointer"),
    event: Schema.Literals(["move", "down", "up", "click"]),
    x: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8192 })),
    y: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8192 })),
    button: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 5 }))),
  }),
  Schema.Struct({
    type: Schema.Literal("key"),
    event: Schema.Literals(["press", "down", "up"]),
    key: TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
    modifiers: Schema.optional(
      Schema.Array(Schema.Literals(["alt", "ctrl", "meta", "shift"])).check(Schema.isMaxLength(4)),
    ),
  }),
  Schema.Struct({
    type: Schema.Literal("text"),
    text: Schema.String.check(Schema.isNonEmpty()).check(Schema.isMaxLength(8_192)),
  }),
  Schema.Struct({
    type: Schema.Literal("scroll"),
    x: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8192 }))),
    y: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8192 }))),
    deltaX: Schema.Int.check(Schema.isBetween({ minimum: -4096, maximum: 4096 })),
    deltaY: Schema.Int.check(Schema.isBetween({ minimum: -4096, maximum: 4096 })),
  }),
]);
export type VmDesktopInputAction = typeof VmDesktopInputAction.Type;

export const VmDesktopInput = Schema.Struct({
  threadId: ThreadId,
  action: VmDesktopInputAction,
});
export type VmDesktopInput = typeof VmDesktopInput.Type;

export class VmUnavailableError extends Schema.TaggedErrorClass<VmUnavailableError>()(
  "VmUnavailableError",
  {
    message: Schema.String,
  },
) {}

export class VmNotFoundError extends Schema.TaggedErrorClass<VmNotFoundError>()("VmNotFoundError", {
  threadId: ThreadId,
}) {}

export class VmOperationError extends Schema.TaggedErrorClass<VmOperationError>()(
  "VmOperationError",
  {
    operation: TrimmedNonEmptyString,
    message: Schema.String,
  },
) {}

export class VmControlError extends Schema.TaggedErrorClass<VmControlError>()("VmControlError", {
  message: Schema.String,
}) {}

export const VmError = Schema.Union([
  VmUnavailableError,
  VmNotFoundError,
  VmOperationError,
  VmControlError,
]);
export type VmError = typeof VmError.Type;
